import type { Address, Hash } from 'viem'
import { encodeFunctionData, toFunctionSelector } from 'viem'
import { contractBook } from '@eulerxyz/core-sdk'
import type { ContractFunctionConfig, SuccessBatchResult } from './types'
import type { BatchResult, ContractFunctionResult, FailureBatchResult } from './types'

const PERMIT2_ADDRESSES = new Set(Object.values(contractBook.permit2.address).map((a: string) => a.toLowerCase()))

const PERMIT2_TRANSFER_FROM_SELECTORS = new Set([
  toFunctionSelector('transferFrom(address,address,uint160,address)'),
  toFunctionSelector('transferFrom((address,address,uint160,address)[])'),
])

export type EVCCall = {
  data: Hash
  onBehalfOfAccount: Address
  targetContract: Address
  value: bigint
}

export type EVCCalls = EVCCall[]

export function isEVCCall(call: unknown): call is EVCCall {
  return (
    typeof call === 'object' &&
    call !== null &&
    'data' in call &&
    'onBehalfOfAccount' in call &&
    'targetContract' in call &&
    'value' in call
  )
}

/**
 * SECURITY: Reject any EVC batch item that calls Permit2.transferFrom.
 *
 * When executed via EVC.batch(), msg.sender for the target contract is the EVC.
 * If Permit2.transferFrom is in the batch, Permit2 treats the EVC as the spender.
 * This means ANY user with a Permit2 allowance where spender=EVC could be drained
 * by an attacker calling EVC.call(Permit2, attackerAddr, 0, transferFrom(victim,...)).
 *
 * Permit2.permit() IS allowed in batches (it sets up allowances for vaults/swappers).
 * Only transferFrom is blocked because it would make EVC the msg.sender/spender.
 */
function assertNoPermit2TransferFrom(call: EVCCall): void {
  const selector = call.data.slice(0, 10).toLowerCase() as `0x${string}`
  if (PERMIT2_ADDRESSES.has(call.targetContract.toLowerCase()) && PERMIT2_TRANSFER_FROM_SELECTORS.has(selector)) {
    throw new Error(
      'SECURITY: EVC batch must not contain Permit2.transferFrom. ' +
        'When called via EVC batch, msg.sender=EVC, making EVC the Permit2 spender. ' +
        'This creates a vulnerability where any EVC.call() user can drain tokens. ' +
        'Use vault.deposit() (which calls Permit2 internally with msg.sender=vault) instead.',
    )
  }
}

export function mapToEVCCall(contracts: (ContractFunctionConfig | EVCCall)[]): EVCCalls {
  const calls: EVCCalls = []

  if (contracts === undefined) {
    console.warn('[mapToEVCCall] contracts is undefined')
  }
  for (let i = 0; i < contracts.length; i++) {
    const contract = contracts[i]
    if (!contract) continue
    if (isEVCCall(contract)) {
      assertNoPermit2TransferFrom(contract)
      calls.push(contract)
      continue
    }
    const { abi, address, args, functionName, onBehalfOfAccount, value } = contract

    const callData = encodeFunctionData({
      abi,
      args,
      functionName,
    })

    const evcCall: EVCCall = {
      data: callData,
      targetContract: address,
      onBehalfOfAccount,
      value,
    }
    assertNoPermit2TransferFrom(evcCall)
    calls.push(evcCall)
  }

  return calls
}

export function isFailureBatchResult(result: BatchResult<ContractFunctionResult>): result is FailureBatchResult {
  return result.status === 'failure'
}

export function isSuccessBatchResult(
  result: BatchResult<ContractFunctionResult>,
): result is SuccessBatchResult<ContractFunctionResult> {
  return result.status === 'success'
}
